import { useEffect, useMemo } from "react";
import EndpointStatusChart from "../../../../SiteExplorer/Pages/Endpoints/EndpointStatusChart";
import NoDataPage from "../../Pages/NoDataPage";
import { enMessages } from "../../../../../messages/en-US";
import { useLocalization } from "@progress/kendo-react-intl";
import { useQuery } from "react-query";
import healthDashboardApiService from "../../Utilities/HealthDashboardApiService";
import { Loader } from "@progress/kendo-react-indicators";
import ErrorCard from "../../../../Shared/Components/Cards/ErrorCard";

interface Props {
  deviceData: any;
  selectedEndpointId: string;
  eventFilter: any;
  setEventFilter: any;
  chartParentHeight: number;
  deviceInfo: any;
  deviceNotifications: any;
  /** Reports whether the timeline has any series to draw. */
  onHasData?: (hasData: boolean) => void;
}

export default function GatewayHealthDetails(props: Props) {
    const localizationService = useLocalization();

    const deviceStateEventsQuery = useQuery<any>(["deviceStateEvents", props.deviceData.serialNumber, props.eventFilter], async () => {
        return await healthDashboardApiService.getDeviceStateEvents(props.deviceData.serialNumber, props.eventFilter);
    });

    const statusData = {
        dateFilter: props.eventFilter,
        timestamps: props.deviceNotifications?.timestamps,
        alerts: {
             timestamps: props.deviceNotifications?.timestamps,
             yValues: new Array(props.deviceNotifications?.timestamps?.length).fill(1),
             metadata: props.deviceNotifications?.metadata
         },
         statusData: {
             id: "",
             timestamps: [],
             yValues: [],
             metadata:[
                 {
                     color: "#FFFFFF",
                     reasonCode: 0
                 },
                 {
                     color: "#00A2E8",
                     reasonCode: 0
                 }
             ]
         }
     }

    const timelineData = useMemo(() => {
        const seriesData: any[] = [];
        const eventsData = deviceStateEventsQuery.data;
        const deviceInfoData : any[] = props.deviceInfo;

        // Add device info series if deviceInfo exists
        if (deviceInfoData.length > 0 && deviceInfoData.length > 0) {
            // Extract timestamps and convert to Unix timestamps
            const deviceTimestamps = deviceInfoData.map(device => 
                Math.floor(new Date(device.timestampUtc).getTime() / 1000)
            );

            // Extract device information for each data point
            const serialNumbers = deviceInfoData.map(device => device?.serialNo || "Unknown");
            const firmwareVersions = deviceInfoData.map(device => device?.firmwareVersion || "Unknown");
            const modelNumbers = deviceInfoData.map(device => device?.modelNo || "Unknown");

            seriesData.push({
                id: "GatewayId",
                name: localizationService.toLanguageString('custom.deviceInfoEvents', enMessages.custom.deviceInfoEvents),
                color: "#FF7F27",
                timestamps: deviceTimestamps,
                yAxisValueArray: new Array(deviceTimestamps?.length).fill(1),
                tooltips: [{
                    name: localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber),
                    data: serialNumbers,
                    color: "#FF7F27"
                },
                {
                    name: localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion),
                    data: firmwareVersions,
                    color: "#FF7F27"
                },
                {
                    name: localizationService.toLanguageString('custom.modelNumber', enMessages.custom.modelNumber),
                    data: modelNumbers,
                    color: "#FF7F27"
                }]
            });
        }

        // Add state events series if state events exist
        if (eventsData && eventsData.length > 0) {
            seriesData.push({
                id: "GreasePumpEventsId",
                name: localizationService.toLanguageString('custom.greasePumpEvents', enMessages.custom.greasePumpEvents),
                color: "#1E8449",
                timestamps: eventsData.map((event: any) => 
                    Math.floor(new Date(event.timestamp).getTime() / 1000)
                ),
                yAxisValueArray: new Array(eventsData.length).fill(deviceInfoData.length > 0 ? 2 : 1),
                tooltips: [
                    {
                        name: localizationService.toLanguageString('custom.status', enMessages.custom.status),
                        data: eventsData.map((event: any) => event.value === 1 ? 'On' : 'Off'),
                        color: "#1E8449"
                    },
                    {
                        name: localizationService.toLanguageString('custom.channel', enMessages.custom.channel),
                        data: eventsData.map((event: any) => event.channel),
                        color: "#1E8449"
                    },
                    {
                        name: localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber),
                        data: eventsData.map((event: any) => event.serialNumber),
                        color: "#1E8449"
                    },
                    {
                        name: localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion),
                        data: eventsData.map((event: any) => event.firmwareVersion),
                        color: "#1E8449"
                    }
                ]
            });
        }

        return {
            seriesData,
            dateFilter: props.eventFilter,
            aggregator: 'none',
            strokeThickness: 1
        };
      }, [props.deviceInfo, props.eventFilter, deviceStateEventsQuery.data, localizationService]);

    const isLoadingEvents = deviceStateEventsQuery.isLoading || deviceStateEventsQuery.isFetching;
    const { onHasData } = props;

    // Treat "still loading" as having data, so the toolbar does not flicker.
    useEffect(() => {
        onHasData?.(isLoadingEvents || timelineData.seriesData.length > 0);
    }, [onHasData, isLoadingEvents, timelineData.seriesData.length]);

  if(isLoadingEvents) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader type="pulsing" />
      </div>
    );
  }

  if(deviceStateEventsQuery.isError) {
    return (
      <div className="flex justify-center items-center h-full">
        <ErrorCard />
      </div>
    );
  }

  return (
    <div className="legend-column">
    {timelineData.seriesData.length > 0 ?(
      <EndpointStatusChart 
        chartType="timeline"
        timelineData={timelineData}
        isSimpleChart={false}   
        chartParentHeight={props.chartParentHeight}
        statusChartData={statusData}
        showStatus={false}
        isLegendColumn={true}
      />
      ) : (
        <div className="flex justify-center items-center h-full">
          <NoDataPage />
        </div>
      )}
    </div>
  );
}